// Pure helpers for stepping date filters backwards/forwards with arrow buttons.
// All dates are local calendar dates as 'YYYY-MM-DD' strings. No side effects.

const DAY_MS = 86400000;

function parseDate(value) {
    if (typeof value !== 'string') return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) return null;
    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const day = parseInt(match[3], 10);
    if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) return null;
    return { year, month, day };
}

function daysInMonth(year, month) {
    return new Date(year, month, 0).getDate();
}

function formatDate(year, month, day) {
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

// Whole calendar days between two parsed dates, immune to DST.
function dayDiff(a, b) {
    return Math.round((Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day)) / DAY_MS);
}

function addDays(d, days) {
    const date = new Date(d.year, d.month - 1, d.day + days);
    return formatDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

function addMonths(d, months) {
    const index = (d.year * 12) + (d.month - 1) + months;
    const year = Math.floor(index / 12);
    const month = (index % 12) + 1;
    return formatDate(year, month, Math.min(d.day, daysInMonth(year, month)));
}

// Shift a 'YYYY-MM-DD' date by one unit ('day' | 'month' | 'year') in `direction` (1 or -1).
// Month/year steps keep the day of month, clamped to the target month's last day.
// Invalid input is returned unchanged.
export function shiftDate(value, unit = 'day', direction = 1) {
    const d = parseDate(value);
    if (!d) return value;
    if (unit === 'month') return addMonths(d, direction);
    if (unit === 'year') return addMonths(d, direction * 12);
    return addDays(d, direction);
}

// Shift the date part of a 'YYYY-MM-DDThh:mm' value by whole days, keeping the clock time.
export function shiftDateTime(value, days = 1) {
    if (typeof value !== 'string') return value;
    const match = /^(\d{4}-\d{2}-\d{2})(.*)$/.exec(value);
    if (!match || !parseDate(match[1])) return value;
    return `${shiftDate(match[1], 'day', days)}${match[2]}`;
}

// Returns { year, month } when from..to is exactly one calendar month, else null.
export function fullMonthOf(from, to) {
    const f = parseDate(from);
    const t = parseDate(to);
    if (!f || !t) return null;
    if (f.year !== t.year || f.month !== t.month) return null;
    if (f.day !== 1 || t.day !== daysInMonth(t.year, t.month)) return null;
    return { year: f.year, month: f.month };
}

function isFullYear(f, t) {
    return f.year === t.year && f.month === 1 && f.day === 1 && t.month === 12 && t.day === 31;
}

// Step a from/to filter one "period" in `direction` (1 or -1):
// - exactly one calendar month -> neighbouring whole month
// - exactly one calendar year  -> neighbouring whole year
// - any other valid range      -> shifted by its own length (single day = 1 day)
// - only one side set          -> that side shifted one day
// Returns { from, to }, or null when there is nothing to step.
export function shiftDateRange(from, to, direction = 1) {
    const f = parseDate(from);
    const t = parseDate(to);

    if (!f && !t) return null;
    if (!f) return { from, to: shiftDate(to, 'day', direction) };
    if (!t) return { from: shiftDate(from, 'day', direction), to };

    if (fullMonthOf(from, to)) {
        const newFrom = addMonths(f, direction);
        const n = parseDate(newFrom);
        return { from: newFrom, to: formatDate(n.year, n.month, daysInMonth(n.year, n.month)) };
    }

    if (isFullYear(f, t)) {
        return { from: formatDate(f.year + direction, 1, 1), to: formatDate(t.year + direction, 12, 31) };
    }

    const span = dayDiff(f, t);
    const step = span >= 0 ? (span + 1) * direction : direction;
    return { from: addDays(f, step), to: addDays(t, step) };
}
